import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, ButtonGroup, Card, Col, Form, Row, Spinner } from 'react-bootstrap'
import { FiAlertCircle, FiGlobe, FiInfo, FiSearch } from 'react-icons/fi'
import { FaArrowTrendUp } from 'react-icons/fa6'
import useFetch from '../../../_fetch'
import { formatLocalDateForInput, formatNumber, formatNumberLocale, getColor, getLocalDateEnd, getLocalDateStart } from '../../../_utils'
import DateRangeSelector from '../home/dashboard-content/date-range-selector'
import './growth-opportunities.css'

export type ClickDestination = {
    domain: string
    status: 'open' | 'resolved'
    clicks: number
    uncoveredClicks?: number
    revenueShareClicks?: number
    shareOfClicks: number
    firstSeenAt: string | null
    lastSeenAt: string | null
    pageCount: number
    sourcePages: Array<{ page: string; clicks: number }>
    estimatedCommission: number | null
    estimatedUncoveredCommission?: number | null
    estimatedRevenueShareRecovery?: number | null
    estimateIncomplete?: boolean
    affiliateNetworks?: string[]
    affiliateCampaignAvailable?: boolean
}

export type ClickDestinationsResponse = {
    period: {
        fromDate: string
        toDate: string
    }
    summary: {
        totalDomains: number
        openClicks: number
        resolvedClicks: number
        openRevenueShareClicks?: number
        resolvedRevenueShareClicks?: number
        openDomains: number
        resolvedDomains: number
        openSourcePages: number
    }
    valuation: {
        currency: string
        epcWindow: {
            fromDate: string
            toDate: string
            days: number
        }
        clicks: number
        commission: number
        averageEpc: number | null
        automatedCommission: number
        automatedNetworkCommission: number
        totalNetworkCommission: number
        clickPartnershipCommission: number
        estimatedMissedCommission: number | null
        estimatedFutureCommission?: number | null
        estimateIncomplete?: boolean
        revenueShareOptimization?: {
            epcClicks: number
            commission: number
            averageEpc: number | null
            epcSource?: 'network' | 'account' | null
            intermediaryShare: number
            estimatedRecoverableCommission: number | null
        }
    }
    destinations: ClickDestination[]
    diagnostics: {
        totalRecordedClicks: number
        invalidTargetClicks: number
        filteredClicks: number
        filteredDomains: number
        coveredClicks: number
        coveredDomains: number
        trackingLinkClicks: number
        trackingLinkDomains: number
        invalidRevenueShareClicks?: number
        destinationsTotal?: number
        destinationsReturned?: number
        destinationLimitPerStatus?: number
        destinationsTruncated?: boolean
    }
}

type DestinationSort = 'clicks' | 'recent' | 'domain'
type OpportunitySegment = 'open' | 'resolved' | 'all'
const DESTINATIONS_PAGE_SIZE = 50

function hasKnownAffiliateCampaign(destination: ClickDestination) {
    return !!destination.affiliateNetworks?.length || destination.affiliateCampaignAvailable === true
}

function getScopedDestinationData(data: ClickDestinationsResponse | null, affiliateCampaignsOnly: boolean) {
    if (!data || !affiliateCampaignsOnly) {
        return { destinations: data?.destinations || [], summary: data?.summary }
    }

    const destinations = data.destinations.filter(hasKnownAffiliateCampaign)
    const openDestinations = destinations.filter((destination) => destination.status === 'open')
    const resolvedDestinations = destinations.filter((destination) => destination.status === 'resolved')
    const openOpportunityClicks = openDestinations.reduce((sum, destination) => sum + destination.clicks, 0)
    const resolvedOpportunityClicks = resolvedDestinations.reduce((sum, destination) => sum + destination.clicks, 0)
    const openSourcePages = new Set(openDestinations.flatMap((destination) => (
        destination.sourcePages.map((sourcePage) => sourcePage.page)
    )))

    return {
        destinations: destinations.map((destination) => {
            const statusTotal = destination.status === 'resolved'
                ? resolvedOpportunityClicks
                : openOpportunityClicks
            return {
                ...destination,
                shareOfClicks: statusTotal > 0 ? destination.clicks / statusTotal : 0,
            }
        }),
        summary: {
            ...data.summary,
            totalDomains: destinations.length,
            openClicks: openDestinations.reduce((sum, destination) => (
                sum + (destination.uncoveredClicks ?? destination.clicks)
            ), 0),
            resolvedClicks: resolvedDestinations.reduce((sum, destination) => (
                sum + (destination.uncoveredClicks ?? destination.clicks)
            ), 0),
            openRevenueShareClicks: openDestinations.reduce((sum, destination) => (
                sum + (destination.revenueShareClicks || 0)
            ), 0),
            resolvedRevenueShareClicks: resolvedDestinations.reduce((sum, destination) => (
                sum + (destination.revenueShareClicks || 0)
            ), 0),
            openDomains: openDestinations.length,
            resolvedDomains: resolvedDestinations.length,
            openSourcePages: openSourcePages.size,
        },
    }
}

function formatDate(value: string | null) {
    if (!value) return '—'
    const date = new Date(value)
    if (!Number.isFinite(date.getTime())) return '—'
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

const FinancialKpiCard: React.FC<{
    label: string
    value: string
    valueLabel: string
    secondaryValue?: string
    secondaryLabel?: string
    tone: 'generated' | 'potential' | 'total'
}> = ({ label, value, valueLabel, secondaryValue, secondaryLabel, tone }) => (
    <Card className={`growth-opportunities-financial-kpi growth-opportunities-financial-kpi-${tone} border-0 h-100`}>
        <Card.Body>
            <div className="growth-opportunities-financial-label">{label}</div>
            <div className={`growth-opportunities-financial-metrics${secondaryValue == null ? ' growth-opportunities-financial-metrics-single' : ''}`}>
                <div>
                    <div className="growth-opportunities-financial-value">{value}</div>
                    <div className="growth-opportunities-financial-detail">{valueLabel}</div>
                </div>
                {secondaryValue != null && (
                    <div>
                        <div className="growth-opportunities-financial-value growth-opportunities-financial-percentage">
                            <FaArrowTrendUp aria-hidden="true" />
                            <span>{secondaryValue}</span>
                        </div>
                        <div className="growth-opportunities-financial-detail">{secondaryLabel}</div>
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>
)

const GrowthOpportunities: React.FC = () => {
    const { doFetch } = useFetch()
    const [data, setData] = useState<ClickDestinationsResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [sort, setSort] = useState<DestinationSort>('clicks')
    const [segment, setSegment] = useState<OpportunitySegment>('open')
    const [search, setSearch] = useState('')
    const [affiliateCampaignsOnly, setAffiliateCampaignsOnly] = useState(false)
    const [visibleDestinationLimit, setVisibleDestinationLimit] = useState(DESTINATIONS_PAGE_SIZE)
    const [domainsShowingAllSourcePages, setDomainsShowingAllSourcePages] = useState<Set<string>>(() => new Set())
    const [lastRange, setLastRange] = useState<{ startDate: Date; endDate: Date } | null>(null)
    const requestSequence = useRef(0)

    useEffect(() => () => {
        requestSequence.current += 1
    }, [])

    const loadDestinations = useCallback(async (startDate: Date, endDate: Date) => {
        const requestId = ++requestSequence.current
        setLoading(true)
        setError('')
        setVisibleDestinationLimit(DESTINATIONS_PAGE_SIZE)
        setLastRange({ startDate: new Date(startDate), endDate: new Date(endDate) })

        try {
            const response = await doFetch('/byelink/growth-opportunities', {
                fromDate: getLocalDateStart(formatLocalDateForInput(startDate)).toISOString(),
                toDate: getLocalDateEnd(formatLocalDateForInput(endDate)).toISOString(),
                timezoneOffsetMinutes: endDate.getTimezoneOffset(),
            })

            if (response.status >= 400) {
                let message = 'Monetization Audit could not be loaded.'
                try {
                    const payload = await response.json()
                    if (payload?.error) message = payload.error
                } catch {
                    // Keep the safe user-facing fallback.
                }
                throw new Error(message)
            }

            const payload = await response.json()
            if (requestId === requestSequence.current) {
                setData(payload)
                setDomainsShowingAllSourcePages(new Set())
            }
        } catch (err) {
            if (requestId === requestSequence.current) {
                // Do not leave results from an earlier period visible after a
                // failed date-range change.
                setData(null)
                setError(err instanceof Error ? err.message : 'Monetization Audit could not be loaded.')
            }
        } finally {
            if (requestId === requestSequence.current) setLoading(false)
        }
    }, [doFetch])

    const scopedDestinationData = useMemo(
        () => getScopedDestinationData(data, affiliateCampaignsOnly),
        [affiliateCampaignsOnly, data]
    )
    const visibleDestinations = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase()
        const rows = scopedDestinationData.destinations.filter((destination) => (
            (segment === 'all' || destination.status === segment)
            && (!normalizedSearch || destination.domain.includes(normalizedSearch))
        ))

        return [...rows].sort((first, second) => {
            if (sort === 'domain') return first.domain.localeCompare(second.domain)
            if (sort === 'recent') {
                return new Date(second.lastSeenAt || 0).getTime() - new Date(first.lastSeenAt || 0).getTime()
            }
            return second.clicks - first.clicks || first.domain.localeCompare(second.domain)
        })
    }, [scopedDestinationData.destinations, search, segment, sort])
    const displayedDestinations = useMemo(
        () => visibleDestinations.slice(0, visibleDestinationLimit),
        [visibleDestinationLimit, visibleDestinations]
    )

    useEffect(() => {
        setVisibleDestinationLimit(DESTINATIONS_PAGE_SIZE)
    }, [affiliateCampaignsOnly, search, segment, sort])

    const summary = scopedDestinationData.summary
    const unfilteredSummary = data?.summary
    const diagnostics = data?.diagnostics
    const valuation = data?.valuation
    const currency = valuation?.currency || 'EUR'
    const generatedCommission = valuation?.automatedCommission || 0
    const generatedCommissionForComparison = Math.max(0, generatedCommission)
    const revenueShareEpc = valuation?.revenueShareOptimization?.averageEpc ?? valuation?.averageEpc ?? null
    const openUncoveredClicks = summary?.openClicks || 0
    const openRevenueShareClicks = summary?.openRevenueShareClicks || 0
    const unfilteredMissedCommission = valuation?.estimatedMissedCommission == null
        ? null
        : Math.max(0, valuation.estimatedMissedCommission)
    const unfilteredRevenueShareRecovery = openRevenueShareClicks > 0 && revenueShareEpc != null
        ? openRevenueShareClicks * revenueShareEpc * (0.25 / 0.75)
        : null
    const opportunityEstimateIncomplete = affiliateCampaignsOnly
        ? (openUncoveredClicks > 0 && valuation?.averageEpc == null)
            || (openRevenueShareClicks > 0 && revenueShareEpc == null)
        : !!valuation?.estimateIncomplete
    const futureOpportunityCommission = useMemo(() => {
        if (!affiliateCampaignsOnly) {
            if (unfilteredMissedCommission != null || unfilteredRevenueShareRecovery != null) {
                return (unfilteredMissedCommission || 0) + (unfilteredRevenueShareRecovery || 0)
            }
            return valuation?.estimatedFutureCommission == null ? null : Math.max(0, valuation.estimatedFutureCommission)
        }
        if (openUncoveredClicks === 0 && openRevenueShareClicks === 0) return 0

        let estimate = 0
        let hasKnownEstimate = false
        if (openUncoveredClicks > 0 && valuation?.averageEpc != null) {
            estimate += openUncoveredClicks * valuation.averageEpc
            hasKnownEstimate = true
        }
        if (openRevenueShareClicks > 0 && revenueShareEpc != null) {
            estimate += openRevenueShareClicks * revenueShareEpc * (0.25 / 0.75)
            hasKnownEstimate = true
        }
        return hasKnownEstimate ? estimate : null
    }, [
        affiliateCampaignsOnly,
        openRevenueShareClicks,
        openUncoveredClicks,
        revenueShareEpc,
        unfilteredMissedCommission,
        unfilteredRevenueShareRecovery,
        valuation,
    ])
    const totalNetworkCommission = valuation?.totalNetworkCommission || 0
    const automatedNetworkCommission = valuation?.automatedNetworkCommission || 0
    const otherAffiliateCommission = Math.max(0, totalNetworkCommission - automatedNetworkCommission)
    const totalCommission = otherAffiliateCommission + generatedCommissionForComparison
    const potentialTotalCommission = totalCommission + (futureOpportunityCommission || 0)
    const otherAffiliateShare = potentialTotalCommission > 0
        ? otherAffiliateCommission / potentialTotalCommission
        : 0
    const generatedShare = potentialTotalCommission > 0
        ? generatedCommissionForComparison / potentialTotalCommission
        : 0
    const uncoveredShare = potentialTotalCommission > 0
        ? (futureOpportunityCommission || 0) / potentialTotalCommission
        : 0
    const potentialUplift = futureOpportunityCommission != null && totalCommission > 0
        ? futureOpportunityCommission / totalCommission
        : null
    const linkAutomationContribution = totalCommission > 0
        ? generatedCommissionForComparison / totalCommission
        : null
    const auditHeadline = linkAutomationContribution != null
        ? futureOpportunityCommission == null
            ? `Nordpar captured ${formatNumber(generatedCommissionForComparison, currency, 0)} in additional commission`
            : `Nordpar captured ${formatNumber(generatedCommissionForComparison, currency, 0)} in additional commission and identified ${formatNumber(futureOpportunityCommission, currency, 0)} in future potential`
        : futureOpportunityCommission == null
          ? 'Commission and future potential cannot yet be estimated'
          : `Nordpar identified ${formatNumber(futureOpportunityCommission, currency, 0)} in future potential`
    const noRecordedClicks = !!data && (diagnostics?.totalRecordedClicks || 0) === 0
    const noUncoveredDomains = !!data
        && !noRecordedClicks
        && (unfilteredSummary?.totalDomains || 0) === 0
        && (diagnostics?.coveredClicks || 0) > 0
    const noDomainsToReview = !!data
        && !noRecordedClicks
        && !noUncoveredDomains
        && (unfilteredSummary?.totalDomains || 0) === 0
    const getDisplayedEstimatedCommission = (destination: ClickDestination) => {
        const revenueShareClicks = destination.revenueShareClicks || 0
        if (revenueShareClicks === 0) return destination.estimatedCommission
        if (revenueShareEpc == null) return destination.estimatedUncoveredCommission ?? null

        const uncoveredEstimate = destination.estimatedUncoveredCommission
            ?? ((destination.uncoveredClicks || 0) * (valuation?.averageEpc || 0))
        return uncoveredEstimate + revenueShareClicks * revenueShareEpc * (0.25 / 0.75)
    }

    return (
        <div className="container-fluid growth-opportunities-page">
            <div className="link-script-header growth-opportunities-header mb-4">
                <div>
                    <h3 className="mb-1">Monetization Audit</h3>
                </div>
                <div className="growth-opportunities-header-actions">
                    <div className="link-script-date-selector">
                        <DateRangeSelector
                            pageKey="link-script-growth-opportunities"
                            defaultLabel="30 Days"
                            submitInitialRange
                            onSubmit={loadDestinations}
                        />
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={affiliateCampaignsOnly}
                        className={`growth-opportunities-scope-toggle${affiliateCampaignsOnly ? ' growth-opportunities-scope-toggle-active' : ''}`}
                        onClick={() => setAffiliateCampaignsOnly((current) => !current)}
                    >
                        <span className="growth-opportunities-scope-toggle-label">Show only monetizable links</span>
                        <span className="growth-opportunities-scope-toggle-track" aria-hidden="true">
                            <span />
                        </span>
                    </button>
                </div>
            </div>
            <hr />

            {error && (
                <Alert variant="danger" className="growth-opportunities-alert d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div><FiAlertCircle className="me-2" aria-hidden="true" />{error}</div>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={!lastRange || loading}
                        onClick={() => lastRange && loadDestinations(lastRange.startDate, lastRange.endDate)}
                    >
                        Try again
                    </Button>
                </Alert>
            )}

            {loading && !data ? (
                <Card className="growth-opportunities-panel border-0">
                    <Card.Body className="growth-opportunities-loading">
                        <Spinner animation="border" />
                        <div>Loading opportunities…</div>
                    </Card.Body>
                </Card>
            ) : data && (
                <div className={loading ? 'growth-opportunities-is-refreshing' : ''} aria-live="polite">
                    <Row className="g-3 mb-3 growth-opportunities-financial-row">
                        <Col md={4}>
                            <FinancialKpiCard
                                label="Captured by Link Automation"
                                value={formatNumber(generatedCommission, currency)}
                                valueLabel="Commission captured"
                                secondaryValue={linkAutomationContribution == null
                                    ? '—'
                                    : `${formatNumberLocale(linkAutomationContribution * 100, 1)}%`}
                                secondaryLabel={linkAutomationContribution == null
                                    ? 'No network commission recorded'
                                    : 'Of total commission'}
                                tone="generated"
                            />
                        </Col>
                        <Col md={4}>
                            <FinancialKpiCard
                                label="Estimated future opportunity"
                                value={futureOpportunityCommission == null ? '—' : formatNumber(futureOpportunityCommission, currency)}
                                valueLabel={futureOpportunityCommission == null
                                    ? 'Not enough data to estimate'
                                    : opportunityEstimateIncomplete
                                      ? 'Partial estimate from opportunity clicks'
                                      : 'Estimated from opportunity clicks'}
                                secondaryValue={potentialUplift == null
                                    ? '—'
                                    : `+${formatNumberLocale(potentialUplift * 100, 1)}%`}
                                secondaryLabel="Potential uplift"
                                tone="potential"
                            />
                        </Col>
                        <Col md={4}>
                            <FinancialKpiCard
                                label="Total commission"
                                value={formatNumber(totalCommission, currency)}
                                valueLabel="Across all networks and direct partnerships"
                                tone="total"
                            />
                        </Col>
                    </Row>

                    <Card className="growth-opportunities-business-case border-0 mb-3">
                        <Card.Body>
                            <div className="growth-opportunities-business-heading">
                                <div>
                                    <h5>{auditHeadline}</h5>
                                </div>
                            </div>
                            <div
                                className="growth-opportunities-revenue-bar"
                                role="img"
                                aria-label={`${formatNumber(generatedCommissionForComparison, currency)} captured by Link Automation, ${futureOpportunityCommission == null ? 'an unavailable future estimate' : `${formatNumber(futureOpportunityCommission, currency)} in future potential`} and ${formatNumber(otherAffiliateCommission, currency)} other affiliate commission`}
                            >
                                <span className="growth-opportunities-revenue-generated" style={{ width: `${generatedShare * 100}%` }} />
                                <span className="growth-opportunities-revenue-uncovered" style={{ width: `${uncoveredShare * 100}%` }} />
                                <span className="growth-opportunities-revenue-existing" style={{ width: `${otherAffiliateShare * 100}%` }} />
                            </div>
                            <div className="growth-opportunities-revenue-labels">
                                <span><i className="growth-opportunities-legend-generated" />Captured by Link Automation <strong>{formatNumber(generatedCommissionForComparison, currency)}</strong></span>
                                <span><i className="growth-opportunities-legend-potential" />Future opportunity <strong>{futureOpportunityCommission == null ? '—' : formatNumber(futureOpportunityCommission, currency)}</strong></span>
                                <span><i className="growth-opportunities-legend-existing" />Other affiliate commission <strong>{formatNumber(otherAffiliateCommission, currency)}</strong></span>
                            </div>
                            <div className="growth-opportunities-business-metrics">
                                <div><strong>{formatNumberLocale(summary?.openClicks || 0, 0)}</strong><span>Uncovered clicks</span></div>
                                <div><strong>{formatNumberLocale(summary?.openDomains || 0, 0)}</strong><span>Domains without coverage</span></div>
                                <div><strong>{formatNumberLocale(summary?.openSourcePages || 0, 0)}</strong><span>Source pages affected</span></div>
                            </div>
                        </Card.Body>
                    </Card>

                    <div className="growth-opportunities-estimate-note mb-4">
                        <FiInfo aria-hidden="true" />
                        <span>
                            Future opportunity uses your 90-day average EPC{valuation?.averageEpc == null ? '' : ` of ${formatNumber(valuation.averageEpc, currency)}`}. Indirect affiliate links use their own 90-day EPC to estimate recoverable commission. Actual results vary by advertiser, placement and traffic quality.
                        </span>
                    </div>

                    {noRecordedClicks ? (
                        <Card className="growth-opportunities-empty border-0">
                            <Card.Body>
                                <FiGlobe aria-hidden="true" />
                                <h4>No non-automated clicks</h4>
                                <p>No clicks with a valid external destination were recorded in this period.</p>
                            </Card.Body>
                        </Card>
                    ) : noUncoveredDomains ? (
                        <Card className="growth-opportunities-empty border-0">
                            <Card.Body>
                                <FiGlobe aria-hidden="true" />
                                <h4>No uncovered domains</h4>
                                <p>All relevant clicks in this period occurred while an affiliate campaign or partnership was active.</p>
                            </Card.Body>
                        </Card>
                    ) : noDomainsToReview ? (
                        <Card className="growth-opportunities-empty border-0">
                            <Card.Body>
                                <FiAlertCircle aria-hidden="true" />
                                <h4>No domains to review</h4>
                                <p>Clicks were recorded, but they already used affiliate tracking links or their destinations were excluded from this report.</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Card className="growth-opportunities-panel border-0">
                            <Card.Body>
                                <div className="growth-opportunities-list-heading">
                                    <div>
                                        <h5 className="growth-opportunities-section-title">Revenue opportunities</h5>
                                    </div>
                                </div>

                                <ButtonGroup className="growth-opportunities-segments" aria-label="Filter domains by status">
                                    {(['open', 'resolved', 'all'] as OpportunitySegment[]).map((value) => (
                                        <Button
                                            key={value}
                                            size="sm"
                                            variant={segment === value ? 'primary' : 'outline-secondary'}
                                            aria-pressed={segment === value}
                                            onClick={() => setSegment(value)}
                                        >
                                            {value === 'open'
                                                ? `Open (${summary?.openDomains || 0})`
                                                : value === 'resolved'
                                                  ? `Resolved (${summary?.resolvedDomains || 0})`
                                                  : `All (${summary?.totalDomains || 0})`}
                                        </Button>
                                    ))}
                                </ButtonGroup>

                                <div className="growth-opportunities-toolbar">
                                    <div className="growth-opportunities-search">
                                        <FiSearch aria-hidden="true" />
                                        <Form.Control
                                            type="search"
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder="Search domains"
                                            aria-label="Search domains"
                                        />
                                    </div>
                                    <Form.Select
                                        value={sort}
                                        onChange={(event) => setSort(event.target.value as DestinationSort)}
                                        aria-label="Sort domains"
                                        className="growth-opportunities-sort"
                                    >
                                        <option value="clicks">Most clicks</option>
                                        <option value="recent">Most recent</option>
                                        <option value="domain">Domain A–Z</option>
                                    </Form.Select>
                                </div>

                                <div className="growth-opportunities-table-head" aria-hidden="true">
                                    <span>Domain</span>
                                    <span>Clicks</span>
                                    <span>Estimated value</span>
                                    <span>Share</span>
                                    <span>Latest click</span>
                                </div>

                                <div className="growth-opportunities-list">
                                    {displayedDestinations.map((destination) => (
                                        <article className="growth-opportunities-row" key={destination.domain}>
                                            <div className="growth-opportunities-identity">
                                                <div className="growth-opportunities-domain-line">
                                                    <span className="growth-opportunities-domain-icon"><FiGlobe /></span>
                                                    <div>
                                                        <strong>{destination.domain}</strong>
                                                        <span className={`growth-opportunities-status growth-opportunities-status-${destination.status}`}>
                                                            {destination.status === 'resolved' ? 'Resolved' : 'Open'}
                                                        </span>
                                                        {!!destination.affiliateNetworks?.length && (
                                                            <span className="growth-opportunities-affiliate-programs">
                                                                <span className="growth-opportunities-affiliate-programs-label">Available via</span>
                                                                {destination.affiliateNetworks.map((network, index) => {
                                                                    const networkColor = getColor(network, index) || '#64748b'
                                                                    return (
                                                                        <span
                                                                            className="growth-opportunities-network-badge"
                                                                            style={{ borderColor: networkColor, color: networkColor }}
                                                                            key={network}
                                                                        >
                                                                            <i style={{ backgroundColor: networkColor }} aria-hidden="true" />
                                                                            {network}
                                                                        </span>
                                                                    )
                                                                })}
                                                            </span>
                                                        )}
                                                        {!destination.affiliateNetworks?.length && destination.affiliateCampaignAvailable && (
                                                            <span className="growth-opportunities-affiliate-programs">
                                                                <span className="growth-opportunities-affiliate-programs-label">Available via</span>
                                                                <span
                                                                    className="growth-opportunities-network-badge"
                                                                    style={{ borderColor: getColor('Skimlinks', 0), color: getColor('Skimlinks', 0) }}
                                                                >
                                                                    <i style={{ backgroundColor: getColor('Skimlinks', 0) }} aria-hidden="true" />
                                                                    Skimlinks
                                                                </span>
                                                            </span>
                                                        )}
                                                        {(destination.revenueShareClicks || 0) > 0 && (
                                                            <span className="growth-opportunities-click-breakdown">
                                                                {formatNumberLocale(destination.revenueShareClicks || 0, 0)} indirect affiliate click{destination.revenueShareClicks === 1 ? '' : 's'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {!!destination.sourcePages.length && (
                                                    <details className="growth-opportunities-pages">
                                                        <summary>{destination.pageCount} source page{destination.pageCount === 1 ? '' : 's'}</summary>
                                                        <ul>
                                                            {(domainsShowingAllSourcePages.has(destination.domain)
                                                                ? destination.sourcePages
                                                                : destination.sourcePages.slice(0, 5)
                                                            ).map((page) => (
                                                                <li key={page.page} title={page.page}>
                                                                    <span>{page.page}</span>
                                                                    <strong>{formatNumberLocale(page.clicks, 0)}</strong>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        {destination.sourcePages.length > 5 && (
                                                            <button
                                                                type="button"
                                                                className="growth-opportunities-pages-toggle"
                                                                onClick={() => setDomainsShowingAllSourcePages((current) => {
                                                                    const next = new Set(current)
                                                                    if (next.has(destination.domain)) next.delete(destination.domain)
                                                                    else next.add(destination.domain)
                                                                    return next
                                                                })}
                                                            >
                                                                {domainsShowingAllSourcePages.has(destination.domain)
                                                                    ? 'Show top 5'
                                                                    : `Show all ${destination.sourcePages.length} source pages`}
                                                            </button>
                                                        )}
                                                    </details>
                                                )}
                                            </div>
                                            <div className="growth-opportunities-clicks" data-label="Clicks">
                                                <strong>{formatNumberLocale(destination.clicks, 0)}</strong>
                                                <span>selected period</span>
                                            </div>
                                            <div className="growth-opportunities-estimated-value" data-label="Estimated value">
                                                <strong>{getDisplayedEstimatedCommission(destination) == null
                                                    ? '—'
                                                    : formatNumber(getDisplayedEstimatedCommission(destination) || 0, currency)}</strong>
                                                {(destination.revenueShareClicks || 0) === 0 && (
                                                    <span>{destination.estimateIncomplete
                                                        ? 'partial estimate'
                                                        : destination.status === 'resolved'
                                                          ? 'historical estimate'
                                                          : 'missed commission'}</span>
                                                )}
                                                {(destination.revenueShareClicks || 0) > 0 && (
                                                    <span className="growth-opportunities-value-note">
                                                        Potential gain from replacing Skimlinks with direct affiliate network partnerships.
                                                    </span>
                                                )}
                                            </div>
                                            <div className="growth-opportunities-impact" data-label="Share">
                                                <strong>{formatNumberLocale(destination.shareOfClicks * 100, 1)}%</strong>
                                                <div><span style={{ width: `${Math.max(2, destination.shareOfClicks * 100)}%` }} /></div>
                                                <small>{destination.status === 'resolved' ? 'of resolved opportunity clicks' : 'of open opportunity clicks'}</small>
                                            </div>
                                            <div className="growth-opportunities-last-seen" data-label="Latest click">
                                                <strong>{formatDate(destination.lastSeenAt)}</strong>
                                                <span>First click {formatDate(destination.firstSeenAt)}</span>
                                            </div>
                                        </article>
                                    ))}
                                </div>

                                {displayedDestinations.length < visibleDestinations.length && (
                                    <div className="growth-opportunities-load-more">
                                        <span>
                                            Showing {formatNumberLocale(displayedDestinations.length, 0)} of {formatNumberLocale(visibleDestinations.length, 0)} loaded domains
                                        </span>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => setVisibleDestinationLimit((current) => current + DESTINATIONS_PAGE_SIZE)}
                                        >
                                            Show 50 more
                                        </Button>
                                    </div>
                                )}

                                {!visibleDestinations.length && (
                                    <div className="growth-opportunities-filter-empty">
                                        {search.trim()
                                            ? affiliateCampaignsOnly
                                                ? 'No domains with affiliate campaigns match your search.'
                                                : 'No domains match your search.'
                                            : affiliateCampaignsOnly
                                              ? 'No domains with affiliate campaigns match this filter.'
                                            : segment === 'open'
                                              ? 'All domains in this period are resolved.'
                                              : segment === 'resolved'
                                                ? 'No domains have been resolved yet.'
                                                : 'No domains to display.'}
                                    </div>
                                )}

                                {(diagnostics?.invalidTargetClicks || 0) > 0 && (
                                    <div className="growth-opportunities-diagnostic-note">
                                        <FiAlertCircle aria-hidden="true" />
                                        <span>
                                            {formatNumberLocale(diagnostics?.invalidTargetClicks || 0, 0)} clicks are not shown because their target URL did not contain a valid domain.
                                        </span>
                                    </div>
                                )}

                                {(diagnostics?.trackingLinkClicks || 0) > 0 && (
                                    <div className="growth-opportunities-diagnostic-note">
                                        <FiInfo aria-hidden="true" />
                                        <span>
                                            {formatNumberLocale(diagnostics?.trackingLinkClicks || 0, 0)} clicks are not shown because they already used affiliate tracking links.
                                        </span>
                                    </div>
                                )}
                                {(diagnostics?.invalidRevenueShareClicks || 0) > 0 && (
                                    <div className="growth-opportunities-diagnostic-note">
                                        <FiInfo aria-hidden="true" />
                                        <span>
                                            {formatNumberLocale(diagnostics?.invalidRevenueShareClicks || 0, 0)} indirect affiliate clicks are not shown because their merchant destination could not be identified.
                                        </span>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                </div>
            )}
        </div>
    )
}

export default GrowthOpportunities
